import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, useWindowDimensions, View } from 'react-native';
import { usePathname, useRouter } from 'expo-router';

import { isTabletUp } from '@/lib/design-system/breakpoints';
import {
  getSidebarCollapsed,
  getSidebarWidth,
  setSidebarCollapsed,
  setSidebarWidth,
  SIDEBAR_COLLAPSED_RAIL,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
} from '@/lib/layout/sidebar-prefs';

import { SearchNavProvider } from '@/lib/search/search-nav-context';

import { Sidebar } from './Sidebar';
import { SidebarChevronIcon } from './SidebarIcons.web';
import { SidebarProvider } from './SidebarContext';
import { SidebarResizeHandle } from './SidebarResizeHandle';

type AppShellProps = {
  children: ReactNode;
};

function activePageIdFromPath(pathname: string): string | null {
  const match = pathname.match(/\/page\/([^/?]+)/);
  return match?.[1] ?? null;
}

function isFixedHeightRoute(pathname: string): boolean {
  return pathname.startsWith('/chat') || pathname.startsWith('/search');
}

function AppShellInner({ children }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => getSidebarCollapsed());
  const [sidebarWidth, setSidebarWidthState] = useState(() => getSidebarWidth());
  const widthRef = useRef(sidebarWidth);
  widthRef.current = sidebarWidth;

  useEffect(() => {
    setCollapsed(getSidebarCollapsed());
    setSidebarWidthState(getSidebarWidth());
  }, []);

  const pathname = usePathname();
  const router = useRouter();
  const { width: viewportWidth } = useWindowDimensions();
  const isWide = isTabletUp(viewportWidth);
  const isSearchTab = pathname.startsWith('/search');
  const activePageId = activePageIdFromPath(pathname);
  const fixedHeightMain = isFixedHeightRoute(pathname);

  useEffect(() => {
    if (isSearchTab && collapsed) {
      setCollapsed(false);
      setSidebarCollapsed(false);
    }
  }, [isSearchTab, collapsed]);

  const onResize = useCallback((width: number) => {
    const next = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width));
    setSidebarWidthState(next);
    setSidebarWidth(next);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((value) => {
      const next = !value;
      setSidebarCollapsed(next);
      return next;
    });
  }, []);

  const sidebarCollapsed = collapsed && isWide && !isSearchTab;
  const panelWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_RAIL : sidebarWidth;
  const showSidebar = isWide || drawerOpen;

  const sidebarContent =
    sidebarCollapsed ? (
      <View className="h-full items-center border-r border-border bg-surface pt-2">
        <Pressable
          className="flex h-[22px] w-[22px] items-center justify-center rounded"
          onPress={toggleCollapsed}
        >
          <SidebarChevronIcon />
        </Pressable>
      </View>
    ) : (
      <View className="relative flex h-full min-h-0 flex-col overflow-hidden border-r border-border">
        <Sidebar
          activePageId={activePageId}
          onCollapse={isWide ? toggleCollapsed : undefined}
          onNavigate={() => setDrawerOpen(false)}
        />
        {isWide ? (
          <SidebarResizeHandle getWidth={() => widthRef.current} onResize={onResize} />
        ) : null}
      </View>
    );

  const mainColumn = (
    <>
      {!isWide ? (
        <View className="flex-row items-center justify-between border-b border-border px-3 py-2">
          <Pressable className="px-1 py-0.5" onPress={() => setDrawerOpen((open) => !open)}>
            <Text className="text-lg text-fg">☰</Text>
          </Pressable>
          <Pressable className="px-1 py-0.5" onPress={() => router.push('/settings')}>
            <Text className="text-xs text-muted">Settings</Text>
          </Pressable>
        </View>
      ) : null}
      {children}
    </>
  );

  return (
    <View className={`flex h-full flex-1 flex-row bg-surface ${Platform.OS === 'web' ? 'max-h-screen' : 'min-h-screen'}`}>
      {showSidebar ? (
        <View
          className={`flex h-full min-h-0 flex-col ${isWide ? 'shrink-0' : 'absolute bottom-0 left-0 top-0 z-50'}`}
          style={{ width: panelWidth }}
        >
          {sidebarContent}
        </View>
      ) : null}

      {!isWide && drawerOpen ? (
        <Pressable
          className="absolute inset-0 z-40 bg-black/40"
          onPress={() => setDrawerOpen(false)}
        />
      ) : null}

      <View className={`min-w-0 flex-1 ${Platform.OS === 'web' ? 'min-h-0' : 'overflow-visible'}`}>
        {Platform.OS === 'web' && !fixedHeightMain ? (
          <ScrollView
            className="min-h-0 flex-1"
            contentContainerClassName="flex-grow"
            showsVerticalScrollIndicator
          >
            {mainColumn}
          </ScrollView>
        ) : (
          <View className={fixedHeightMain ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : 'flex-1'}>
            {mainColumn}
          </View>
        )}
      </View>
    </View>
  );
}

export function AppShell({ children }: AppShellProps) {
  return (
    <SidebarProvider>
      <SearchNavProvider>
        <AppShellInner>{children}</AppShellInner>
      </SearchNavProvider>
    </SidebarProvider>
  );
}
